import { inject, Injectable } from '@angular/core';
import { NGXLogger } from 'ngx-logger';

import { GcdmCustomerService } from '../clients/gcdm/gcdm-customer.service';
import { RetailCrmService } from '../clients/retail-crm/retail-crm.service';
import { Customer } from '../model/customer.model';
import { CustomerMasterDataSource, ownsMasterDataInGcdm } from './customer-source';

export interface CustomerLoadResult {
  customer: Customer;
  /** Where the master data on screen came from. */
  source: CustomerMasterDataSource;
  /**
   * True when the customer is synced but the NSC Database could not be read, so the master data
   * shown is Retail CRM's mirror and may be stale. The page can warn instead of pretending.
   */
  degraded: boolean;
}

/** * Loads a customer profile from the system that owns its master data (CUSTAPP-613, read side). *
 * inSyncWithDirectSales && gcid -> Retail CRM (account, vehicles, consents) + NSC Database (master data)
 * otherwise -> Retail CRM (unchanged behaviour)
 *
 *
 * Retail CRM is always read first, and not only for its own fields: the sync flag and the GCID
 * that decide the routing live there, and so does the account number the page is routed by.
 *
 * The NSC Database read is not allowed to break the page. If it fails, the Retail CRM record
 * is shown and degraded is set: a customer whose profile cannot be opened at all is worse than
 * one whose master data lags behind by a sync cycle. The write path makes the opposite choice -
 * a failed GCDM write is a failure, never a silent Retail CRM write - because writing to the
 * wrong system creates a conflict, while reading from it only shows old data. */

@Injectable({
  providedIn: 'root',
})
export class CustomerLoadService {
  private readonly retailCrmService = inject(RetailCrmService);
  private readonly gcdmCustomerService = inject(GcdmCustomerService);
  private readonly logger = inject(NGXLogger);

  async loadCustomer(accountNumber: string): Promise<CustomerLoadResult> {
    const retailCustomer = await this.retailCrmService.getAccountByAccountNumber(accountNumber);

    if (!ownsMasterDataInGcdm(retailCustomer)) {
      if (retailCustomer.inSyncWithDirectSales && !retailCustomer.gcid) {
        this.logger.warn(
          `#CDFTF0287 Customer ${accountNumber} is flagged as synced with the NSC Database but has no GCID. ` +
            `Showing Retail CRM's data; this customer is also edited in Retail CRM (see CustomerUpdateService).`,
        );
      }
      return { customer: retailCustomer, source: 'RETAIL_CRM', degraded: false };
    }

    try {
      const customer = await this.gcdmCustomerService.getSyncedCustomer(retailCustomer.gcid!, retailCustomer);
      return { customer, source: 'GCDM', degraded: false };
    } catch (error) {
      this.logger.error(
        `#CDFTF0288 Could not read the synced customer ${accountNumber} from the NSC Database under ` +
          `GCID ${retailCustomer.gcid}. Falling back to Retail CRM's mirror, which may be stale.`,
        error,
      );
      return { customer: retailCustomer, source: 'RETAIL_CRM', degraded: true };
    }
  }
}
